'use client'

import { Domains } from 'monitoring/constants/domains'
import { useLog } from 'monitoring/sendLog/client/useLog'
import {
	type Dispatch,
	type SetStateAction,
	createContext,
	useContext,
	useMemo,
	useState,
} from 'react'

export type CustomizationContextType = {
	isCustomizationOpen: boolean
	setIsCustomizationOpen: Dispatch<SetStateAction<boolean>>
}

type CustomizationProviderProps = {
	children: React.ReactNode
}

export const CustomizationContext = createContext<
	CustomizationContextType | undefined
>(undefined)

export function CustomizationProvider({
	children,
}: CustomizationProviderProps): React.ReactNode {
	const [isCustomizationOpen, setIsCustomizationOpen] = useState(false)

	const value = useMemo(
		() => ({
			isCustomizationOpen,
			setIsCustomizationOpen,
		}),
		[isCustomizationOpen, setIsCustomizationOpen]
	)

	return (
		<CustomizationContext.Provider value={value}>
			{children}
		</CustomizationContext.Provider>
	)
}

export const useCustomization = (): CustomizationContextType | undefined => {
	const context = useContext(CustomizationContext)
	const { sendLog } = useLog()

	if (!context) {
		sendLog({
			domain: Domains.PDP,
			message: 'useCustomization must be used within a CustomizationProvider',
		})
	}

	return context
}
